import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Eye, EyeOff } from 'lucide-react';
import { userManager, User } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';
import { authConfig } from '../config/auth';
import '../styles/profile.css';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { token, accessToken, user, setUser } = useAuth();
  const [localUser, setLocalUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setLocalUser(user);
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setProfilePicture(user.profilePicture || '');
    }
    // Refresh user data from backend to get latest points
    userManager.refreshUser(authConfig.apiUrl).then(updatedUser => {
      if (updatedUser) {
        setLocalUser(updatedUser);
        setUser(updatedUser);
        setFirstName(updatedUser.firstName || '');
        setLastName(updatedUser.lastName || '');
        setProfilePicture(updatedUser.profilePicture || '');
      }
    });
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB for S3)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError(`Image size must be less than 5MB (current: ${Math.round(file.size / (1024 * 1024))}MB)`);
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      // Step 1: Get presigned URL from backend
      if (!token) {
        throw new Error('Not authenticated');
      }

      const presignedResponse = await fetch(`${authConfig.apiUrl}/api/upload/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          purpose: 'profile',
        }),
      });

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json();
        throw new Error(errorData.message || 'Failed to get upload URL');
      }

      const { uploadUrl, publicUrl } = await presignedResponse.json();

      // Step 2: Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Step 3: Set the public URL as the profile picture
      setProfilePicture(publicUrl);
      setUploadingImage(false);
    } catch (err: any) {
      console.error('Image upload error:', err);
      setError(err.message || 'Failed to upload image');
      setUploadingImage(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!token) {
        throw new Error('Not authenticated');
      }

      console.log('Sending profile update with token:', token.substring(0, 50) + '...');
      console.log('API URL:', `${authConfig.apiUrl}/api/profile`);

      const response = await fetch(`${authConfig.apiUrl}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName,
          lastName,
          profilePicture,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      // Update local user data
      if (localUser) {
        const updatedUser = {
          ...localUser,
          firstName,
          lastName,
          profilePicture,
        };
        setUser(updatedUser);
        setLocalUser(updatedUser);
      }

      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      if (!token || !accessToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${authConfig.apiUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Use ID token for API Gateway
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          accessToken, // Send access token in body for Cognito
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      setSuccess('Password changed successfully!');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!localUser) {
    return <div className="profile-loading">Loading...</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1 className="profile-title">Profile Settings</h1>
        <p className="profile-subtitle">Manage your account information</p>
      </div>

      {error && <div className="profile-error">{error}</div>}
      {success && <div className="profile-success">{success}</div>}

      {/* Profile Information Section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <h2 className="profile-section-title">Personal Information</h2>
        </div>
        
        <form onSubmit={handleProfileUpdate} className="profile-form">
          {/* Profile Picture Preview */}
          <div className="profile-picture-section">
            <div className="profile-picture-preview">
              {profilePicture ? (
                <img src={profilePicture} alt="Profile" className="profile-picture-img" />
              ) : (
                <div className="profile-picture-placeholder">
                  {firstName?.[0]?.toUpperCase() || localUser?.email[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="profile-picture-info">
              <p className="profile-picture-label">Profile Picture</p>
              <p className="profile-picture-hint">Upload an image (max 5MB)</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage || loading}
                className="profile-file-input"
                id="profile-picture-upload"
              />
              <label htmlFor="profile-picture-upload" className="profile-upload-button">
                {uploadingImage ? 'Uploading...' : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Camera size={18} /> Upload Image (max 5MB)
                  </span>
                )}
              </label>
            </div>
          </div>

          <div className="profile-form-row">
            <div className="profile-form-group">
              <label htmlFor="firstName" className="profile-label">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                required
                className="profile-input"
                disabled={loading}
              />
            </div>

            <div className="profile-form-group">
              <label htmlFor="lastName" className="profile-label">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
                className="profile-input"
                disabled={loading}
              />
            </div>
          </div>

          <div className="profile-form-group">
            <label htmlFor="email" className="profile-label">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={localUser.email}
              disabled
              className="profile-input profile-input-disabled"
            />
            <p className="profile-hint">Email cannot be changed</p>
          </div>

          <div className="profile-form-group">
            <label className="profile-label">Role</label>
            <input
              type="text"
              value={localUser.role}
              disabled
              className="profile-input profile-input-disabled"
            />
          </div>

          <div className="profile-form-group">
            <label className="profile-label">Company</label>
            <input
              type="text"
              value={localUser.companyName ?? localUser.companyId}
              disabled
              className="profile-input profile-input-disabled"
            />
          </div>

          <button type="submit" disabled={loading} className="profile-button">
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Password Change Section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <h2 className="profile-section-title">Password</h2>
        </div>

        {!showPasswordChange ? (
          <button
            onClick={() => setShowPasswordChange(true)}
            className="profile-button-secondary"
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handlePasswordChange} className="profile-form">
            <div className="profile-form-group">
              <label htmlFor="currentPassword" className="profile-label">
                Current Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  className="profile-input"
                  disabled={loading}
                  style={{ paddingRight: '50px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="profile-toggle-password"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="profile-form-group">
              <label htmlFor="newPassword" className="profile-label">
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={8}
                  className="profile-input"
                  disabled={loading}
                  style={{ paddingRight: '50px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="profile-toggle-password"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="profile-hint">
                Must be at least 8 characters with uppercase, lowercase, and numbers
              </p>
            </div>

            <div className="profile-form-group">
              <label htmlFor="confirmPassword" className="profile-label">
                Confirm New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                  className="profile-input"
                  disabled={loading}
                  style={{ paddingRight: '50px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="profile-toggle-password"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="profile-form-actions">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordChange(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                }}
                className="profile-button-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" disabled={loading} className="profile-button">
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

